import { InAppState } from "../base/InAppState";
import '../../components/template/test-center';
import template from './TemplateDetailTemplate.html';
import { Grid, html } from 'gridjs';

export class TemplateDetailState extends InAppState
{
    static ROUTE = '/template/view/:templateId';
    static ID = 'template-detail';

    constructor( app, rp )
    {
        super( app, rp );
        this.templateId = rp.getParam( 'templateId');
        this.generatedDocumentsTable = null;
    }


    async enter()
    {
        this.template = null;

        const { result } = await this.app.api.get( `/template/${this.templateId}` );
        if ( result.status === 200 && result.data )
        {
            this.template = result.data;
        }

        this.render();

        if ( this.template )
        {
            this.addEventListener(
                this.app.view.pageContent.querySelector( '[data-type="template-delete-button"]' ),
                'click',
                async ( e ) => {
                    this.deleteTemplate();
                }
            );

            this._bindToggleStatusButton();

            this.app.view.pageContent.querySelectorAll( '[data-type="template-tab"]' ).forEach( ( tab ) => {
                this.addEventListener( tab, 'click', () => {
                    this.switchTab( tab.getAttribute( 'data-tab-target' ) );
                });
            });

            const testCenter = this.app.view.pageContent.querySelector( 'test-center' );
            if ( testCenter )
            {
                testCenter.template = this.template;
            }

            const placeholdersToggle = this.app.view.pageContent.querySelector( '[data-type="placeholders-toggle"]' );
            if ( placeholdersToggle )
            {
                this.addEventListener( placeholdersToggle, 'click', () => {
                    this._togglePlaceholders();
                });
            }

            this._bindPlaceholderDescriptionEditing();
            this._bindEditButton();
            this._bindTemplateFileDownload();
        }
    }

    _bindEditButton()
    {
        const editBtn = this.app.view.pageContent.querySelector( '[data-type="template-edit-button"]' );
        if ( editBtn )
        {
            this.addEventListener( editBtn, 'click', () => {
                this.app.router.redirect( `/template/edit/${this.templateId}` );
            });
        }
    }

    _bindToggleStatusButton()
    {
        const toggleBtn = this.app.view.pageContent.querySelector( '[data-type="template-toggle-status"]' );
        if ( toggleBtn )
        {
            this.addEventListener( toggleBtn, 'click', async () => {
                this._toggleStatus();
            });
        }
    }

    _rebindEventListeners()
    {
        this.addEventListener(
            this.app.view.pageContent.querySelector( '[data-type="template-delete-button"]' ),
            'click',
            async () => {
                this.deleteTemplate();
            }
        );

        this._bindToggleStatusButton();

        this.app.view.pageContent.querySelectorAll( '[data-type="template-tab"]' ).forEach( ( tab ) => {
            this.addEventListener( tab, 'click', () => {
                this.switchTab( tab.getAttribute( 'data-tab-target' ) );
            });
        });

        const testCenter = this.app.view.pageContent.querySelector( 'test-center' );
        if ( testCenter )
        {
            testCenter.template = this.template;
        }

        const placeholdersToggle = this.app.view.pageContent.querySelector( '[data-type="placeholders-toggle"]' );
        if ( placeholdersToggle )
        {
            this.addEventListener( placeholdersToggle, 'click', () => {
                this._togglePlaceholders();
            });
        }

        this._bindPlaceholderDescriptionEditing();
        this._bindEditButton();
        this._bindTemplateFileDownload();
    }

    _bindTemplateFileDownload()
    {
        const downloadLink = this.app.view.pageContent.querySelector( '[data-type="download-template-file"]' );
        if ( !downloadLink ) return;

        this.addEventListener( downloadLink, 'click', async ( e ) => {
            e.preventDefault();

            const originalText = downloadLink.textContent;
            downloadLink.textContent = 'Downloading...';
            downloadLink.style.pointerEvents = 'none';

            try
            {
                const response = await fetch( `${this.app.api.getUrl()}/template/file/${this.template.id}`, {
                    method: 'GET',
                    headers: {
                        'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                        'x-version': this.app.api._api.headers.get( 'x-version' )
                    }
                });

                if ( !response.ok )
                {
                    throw new Error( 'Download failed' );
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL( blob );
                const a = document.createElement( 'a' );
                a.href = url;
                a.download = this.template.filename;
                document.body.appendChild( a );
                a.click();
                document.body.removeChild( a );
                window.URL.revokeObjectURL( url );
            }
            catch ( error )
            {
                this.app.view.toast( 'Failed to download template file', 'error' );
            }
            finally
            {
                downloadLink.textContent = originalText;
                downloadLink.style.pointerEvents = '';
            }
        });
    }

    _bindPlaceholderDescriptionEditing()
    {
        const descriptionCells = this.app.view.pageContent.querySelectorAll( '[data-type="placeholder-description"]' );

        descriptionCells.forEach( ( cell ) => {
            this.addEventListener( cell, 'click', ( e ) => {
                if ( cell.querySelector( '.description-input' ) )
                {
                    return;
                }
                this._startEditingDescription( cell );
            });
        });
    }

    _startEditingDescription( cell )
    {
        const key = cell.getAttribute( 'data-placeholder-key' );
        const textSpan = cell.querySelector( '.description-text' );
        const placeholderSpan = cell.querySelector( '.description-placeholder' );
        const editIcon = cell.querySelector( '.description-edit-icon' );
        const currentValue = textSpan.textContent || '';

        textSpan.style.display = 'none';
        placeholderSpan.style.display = 'none';
        editIcon.style.display = 'none';

        const input = document.createElement( 'input' );
        input.type = 'text';
        input.className = 'description-input';
        input.value = currentValue;
        input.placeholder = 'Enter description...';
        cell.appendChild( input );
        input.focus();
        input.select();

        const saveDescription = async () => {
            const newValue = input.value.trim();

            if ( newValue === currentValue )
            {
                this._finishEditingDescription( cell, currentValue );
                return;
            }

            cell.classList.add( 'saving' );

            try
            {
                const response = await fetch( `${this.app.api.getUrl()}/template/${this.template.id}/placeholder`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                        'x-version': this.app.api._api.headers.get( 'x-version' )
                    },
                    body: JSON.stringify({ key, description: newValue })
                });

                cell.classList.remove( 'saving' );

                if ( response.ok )
                {
                    const updatedTemplate = await response.json();
                    this.template = updatedTemplate;

                    const testCenter = this.app.view.pageContent.querySelector( 'test-center' );
                    if ( testCenter )
                    {
                        testCenter.template = this.template;
                    }

                    this._finishEditingDescription( cell, newValue );
                    this.app.view.toast( 'Description updated', 'success' );
                }
                else
                {
                    const errorData = await response.json().catch( () => null );
                    this.app.view.toast( errorData?.message || 'Failed to update description', 'error' );
                    this._finishEditingDescription( cell, currentValue );
                }
            }
            catch ( error )
            {
                cell.classList.remove( 'saving' );
                this.app.view.toast( 'An error occurred while updating description', 'error' );
                this._finishEditingDescription( cell, currentValue );
            }
        };

        input.addEventListener( 'blur', saveDescription );
        input.addEventListener( 'keydown', ( e ) => {
            if ( e.key === 'Enter' )
            {
                e.preventDefault();
                input.blur();
            }
            else if ( e.key === 'Escape' )
            {
                e.preventDefault();
                input.removeEventListener( 'blur', saveDescription );
                this._finishEditingDescription( cell, currentValue );
            }
        });
    }

    _finishEditingDescription( cell, value )
    {
        const input = cell.querySelector( '.description-input' );
        const textSpan = cell.querySelector( '.description-text' );
        const placeholderSpan = cell.querySelector( '.description-placeholder' );
        const editIcon = cell.querySelector( '.description-edit-icon' );

        if ( input )
        {
            input.remove();
        }

        textSpan.textContent = value;
        textSpan.style.display = value ? '' : 'none';
        placeholderSpan.textContent = value ? '' : 'Click to add description...';
        placeholderSpan.style.display = value ? 'none' : '';
        editIcon.style.display = '';
    }

    _togglePlaceholders()
    {
        const content = this.app.view.pageContent.querySelector( '[data-type="placeholders-content"]' );
        const icon = this.app.view.pageContent.querySelector( '[data-type="placeholders-toggle-icon"]' );

        if ( content && icon )
        {
            const isVisible = content.style.display !== 'none';
            content.style.display = isVisible ? 'none' : 'block';
            icon.classList.toggle( 'ti-chevron-right', isVisible );
            icon.classList.toggle( 'ti-chevron-down', !isVisible );
        }
    }

    async _toggleStatus()
    {
        const isActive = this.template.status === 'ACTIVE';
        const action = isActive ? 'deactivate' : 'activate';
        const actionLabel = isActive ? 'Deactivate' : 'Activate';

        const confirmed = await this.app.view.confirm(
            `${actionLabel} Template`,
            `Do you want to ${action} this template?`
        );

        if ( confirmed )
        {
            const pageContent = this.app.view.pageContent;
            const message = isActive ? 'Deactivating template...' : 'Activating template...';
            this.app.view.blockUI( pageContent, message );

            const newStatus = isActive ? 'INACTIVE' : 'ACTIVE';
            const { result } = await this.app.api.put(
                `/template/${this.template.id}/status`,
                { status: newStatus }
            );

            this.app.view.unblockUI( pageContent );

            if ( result.status === 200 )
            {
                this.template.status = newStatus;
                this.render();
                this._rebindEventListeners();
                this.app.view.toast( `Template ${action}d successfully`, 'success' );
            }
            else
            {
                this.app.view.toast( `Failed to ${action} template`, 'error' );
            }
        }
    }

    async deleteTemplate()
    {
        const performDeletion = await this.app.view.confirm(
            "Delete Template",
            "Do you really want to delete this template?"
        );

        if ( true === performDeletion )
        {
            const { result } = await this.app.api.delete( `/template/${this.templateId}` );
            if ( result.status === 200 )
            {
                this.app.view.toast( "Template deleted successfully", "success" );
                this.app.router.redirect( '/template/list' );
            }
            else
            {
                this.app.view.toast( "Template deletion failed", "error" );
            }
        }
    }

    switchTab( targetSelector )
    {
        const container = this.app.view.pageContent;

        container.querySelectorAll( '[data-type="template-tab"]' ).forEach( ( tab ) => {
            tab.classList.remove( 'active' );
            tab.setAttribute( 'aria-selected', 'false' );
        });

        container.querySelectorAll( '.tab-pane' ).forEach( ( pane ) => {
            pane.classList.remove( 'show', 'active' );
        });

        const activeTab = container.querySelector( `[data-type="template-tab"][data-tab-target="${targetSelector}"]` );
        if ( activeTab )
        {
            activeTab.classList.add( 'active' );
            activeTab.setAttribute( 'aria-selected', 'true' );
        }

        const activePane = container.querySelector( targetSelector );
        if ( activePane )
        {
            activePane.classList.add( 'show', 'active' );
        }

        if ( targetSelector === '#pills-generated-documents' )
        {
            this._initGeneratedDocumentsTable();
        }
    }

    async _initGeneratedDocumentsTable()
    {
        this._destroyGeneratedDocumentsTable();

        const tableContainer = this.app.view.pageContent.querySelector( '#generated-documents-table' );
        if ( !tableContainer )
        {
            return;
        }

        this.app.view.blockUI( tableContainer, 'Loading documents...' );

        let documents = [];
        try
        {
            const { result } = await this.app.api.get( `/generation/document-list/${this.templateId}` );
            documents = result.status === 200 && result.data?.documents ? result.data.documents : [];
        }
        catch ( error )
        {
            this.app.view.unblockUI( tableContainer );
            this.app.view.toast( 'Failed to load generated documents', 'error' );
            return;
        }

        this.app.view.unblockUI( tableContainer );

        if ( documents.length === 0 )
        {
            tableContainer.innerHTML = `
                <div class="text-center py-5">
                    <i class="ti ti-files display-4 text-muted mb-3"></i>
                    <h5 class="text-muted">No Generated Documents</h5>
                    <p class="text-muted mb-0">Documents generated from this template will appear here.</p>
                </div>
            `;
            this.generatedDocumentsTable = true;
            return;
        }

        const formatFilesize = ( bytes ) => {
            if ( !bytes ) return '---';
            const kb = bytes / 1024;
            if ( kb < 1024 )
            {
                return `${kb.toFixed( 1 )} KB`;
            }
            return `${( kb / 1024 ).toFixed( 1 )} MB`;
        };

        this.generatedDocumentsData = documents.map( doc => [
            doc.filename,
            doc.createdAt ? new Date( doc.createdAt ).toLocaleString() : '---',
            formatFilesize( doc.filesize ),
            doc.filename
        ]);

        this.generatedDocumentsTable = new Grid({
            columns: [
                { name: 'Filename', sort: true },
                { name: 'Generation Date', sort: true },
                { name: 'Filesize', sort: true },
                {
                    id: 'actions',
                    name: '',
                    sort: false,
                    formatter: ( cell ) => {
                        if ( !cell ) return '---';
                        return html( `
                            <div class="d-flex gap-2">
                                <button type="button" data-type="download-document" data-filename="${cell}" class="btn btn-sm btn-outline-primary"><i class="ti ti-download me-1"></i>Download</button>
                                <button type="button" data-type="delete-document" data-filename="${cell}" class="btn btn-sm btn-outline-danger"><i class="ti ti-trash me-1"></i>Delete</button>
                            </div>
                        ` );
                    }
                }
            ],
            data: this.generatedDocumentsData,
            sort: true,
            pagination: {
                limit: 10
            },
            search: true,
            language: {
                search: {
                    placeholder: 'Search...'
                },
                pagination: {
                    previous: 'Previous',
                    next: 'Next',
                    showing: 'Showing',
                    of: 'of',
                    to: 'to',
                    results: 'results'
                },
                noRecordsFound: 'No generated documents found for this template.'
            }
        });

        this.generatedDocumentsTable.render( tableContainer );
        this._bindDocumentDownloadButtons();
    }

    _bindDocumentDownloadButtons()
    {
        const tableContainer = this.app.view.pageContent.querySelector( '#generated-documents-table' );
        if ( !tableContainer ) return;

        this._documentTableClickHandler = async ( e ) => {
            const downloadButton = e.target.closest( '[data-type="download-document"]' );
            const deleteButton = e.target.closest( '[data-type="delete-document"]' );

            if ( downloadButton )
            {
                await this._handleDocumentDownload( downloadButton );
            }
            else if ( deleteButton )
            {
                await this._handleDocumentDelete( deleteButton );
            }
        };

        tableContainer.addEventListener( 'click', this._documentTableClickHandler );
    }

    async _handleDocumentDownload( button )
    {
        const filename = button.getAttribute( 'data-filename' );
        if ( !filename ) return;

        button.disabled = true;
        const originalContent = button.innerHTML;
        button.innerHTML = '<i class="ti ti-loader me-1"></i>Loading...';

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/document/${this.templateId}/${filename}`, {
                method: 'GET',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( 'Download failed' );
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = filename;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
            window.URL.revokeObjectURL( url );
        }
        catch ( error )
        {
            this.app.view.toast( 'Failed to download document', 'error' );
        }
        finally
        {
            button.disabled = false;
            button.innerHTML = originalContent;
        }
    }

    async _handleDocumentDelete( button )
    {
        const filename = button.getAttribute( 'data-filename' );
        if ( !filename ) return;

        const confirmed = await this.app.view.confirm(
            'Delete Document',
            `Are you sure you want to delete "${filename}"?`
        );

        if ( !confirmed ) return;

        button.disabled = true;
        const originalContent = button.innerHTML;
        button.innerHTML = '<i class="ti ti-loader"></i>';

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/document/${this.templateId}/${filename}`, {
                method: 'DELETE',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( 'Delete failed' );
            }

            this.app.view.toast( 'Document deleted successfully', 'success' );

            this.generatedDocumentsData = this.generatedDocumentsData.filter( row => row[0] !== filename );
            this.generatedDocumentsTable.updateConfig({
                data: this.generatedDocumentsData
            }).forceRender();
        }
        catch ( error )
        {
            this.app.view.toast( 'Failed to delete document', 'error' );
            button.disabled = false;
            button.innerHTML = originalContent;
        }
    }

    render()
    {
        this._destroyGeneratedDocumentsTable();

        this.app.view.render(
            this.app.view.pageContent,
            template,
            {
                template: this.template
            }
        );
    }

    _destroyGeneratedDocumentsTable()
    {
        if ( this._documentTableClickHandler )
        {
            const container = this.app.view.pageContent.querySelector( '#generated-documents-table' );
            if ( container )
            {
                container.removeEventListener( 'click', this._documentTableClickHandler );
            }
            this._documentTableClickHandler = null;
        }

        if ( this.generatedDocumentsTable )
        {
            if ( this.generatedDocumentsTable instanceof Grid )
            {
                this.generatedDocumentsTable.destroy();
            }

            const container = this.app.view.pageContent.querySelector( '#generated-documents-table' );
            if ( container )
            {
                container.innerHTML = '';
            }
            this.generatedDocumentsTable = null;
            this.generatedDocumentsData = null;
        }
    }

    getContentHeaderData()
    {
        return {
            title: this.template ? this.template.name : 'Template Details',
            breadcrumb: [ 'Work', 'Templates', this.template ? this.template.name : 'Details' ]
        };
    }

    async exit()
    {
        this._destroyGeneratedDocumentsTable();
    }
}
